import { createSelector } from "reselect";
import { formatCounty } from "components/Assignment/Assignment";

const getStateList = state => state.appraiser.stateList;

export const stateListSelector = createSelector(
  [getStateList],
  stateList => {
    if (stateList) {
      return stateList.map((s) => { return { value: s.id.toString(), label: s.stateName }; });
    }
  }
);

export const stateListAbbreviationSelection = createSelector(
  [getStateList],
  stateList => {
    if (stateList) {
      return stateList.map((s) => { return { label: s.stateName, value: s.stateAbbreviation }; });
    }
  }
);

const getAppraiserManagers = state => state.appraiser.appraiserManagers?.items;

export const appraiserManagersSelector = createSelector(
  [getAppraiserManagers],
  appraiserManagers => {
    if (appraiserManagers) {
      return appraiserManagers.map((m) => { return { value: m.internetUserId.toString(), label: `${m.firstName} ${m.lastName}` }; });
    }
  }
);

const getCustomerServiceAssignToUsers = state => state.customerService.assignToUsers;

export const customerServiceAssignToUsersSelector = createSelector(
  [getCustomerServiceAssignToUsers],
  assignToUsers => {
    if (assignToUsers) {
      return [...assignToUsers].sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);

const getUnderwriters = state => state.customer.underwriters;

export const underwritersSelector = createSelector(
  [getUnderwriters],
  underwriters => {
    if (underwriters) {
      return underwriters.map(x => ({ label: x.displayName, value: x.underwriterCode }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);

const getbusOpsDirectors = state => state.employee.busOpsDirectors;

export const busOpsDirectorsSelector = createSelector(
  [getbusOpsDirectors],
  busOpsDirectors => {
    if (busOpsDirectors) {
      return busOpsDirectors.map(x => ({ label: x.firstLastName, value: x.internetUserId }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);

const getbusOpsManagers = state => state.employee.busOpsManagers;

export const busOpsManagersSelector = createSelector(
  [getbusOpsManagers],
  busOpsManagers => {
    if (busOpsManagers) {
      return busOpsManagers.map(x => ({ label: x.firstLastName, value: x.internetUserId }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);

const getAppraisers = state => state.appraiser.minimalAppraiserList;

export const appraisersSelector = createSelector(
  [getAppraisers],
  appraisers => {
    if (appraisers) {
      return appraisers.map(x => ({ label: x.fullName, value: x.number }));
    }
  }
);

const getAllCustomerActions = state => state.homeownerCustomer.allCustomerActionsForUser;

export const customerActionsSelector = createSelector(
  [getAllCustomerActions],
  actions => {
    if (actions) {
      return actions.map(x => ({ label: x.name, value: x.code }));
    }
  }
);

const getAllCounties = state => state.region.allCounties;

export const allCountiesSelector = createSelector(
  [getAllCounties],
  counties => {
    if (counties) {
      return counties.map(x => ({ label: `${x.state}-${x.name}`, value: x.id })).sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);

const getAllFieldDevelopmentManagers = state => state.employee.fieldDevelopmentManagers;

export const allFieldDevelopmentManagersSelector = createSelector(
  [getAllFieldDevelopmentManagers],
  fieldDevelopmentManagers => {
    if (fieldDevelopmentManagers) {
      return fieldDevelopmentManagers.map(x => ({ label: x.firstLastName, value: x.employeeId }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);

const getAllLenderLogisticsManagers = state => state.employee.lenderLogisticsManagers;
export const allLenderLogisticsManagersSelector = createSelector(
  [getAllLenderLogisticsManagers],
  lenderLogisticsManagers => {
    if (lenderLogisticsManagers) {
      return lenderLogisticsManagers.map(x => ({ label: x.firstLastName, value: x.employeeId }))
      .sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);

const getAllCustomerExperienceManagers = state => state.employee.customerExperienceManagers;

export const allCustomerExperienceManagersSelector = createSelector(
  [getAllCustomerExperienceManagers],
  customerExperienceManagers => {
    if (customerExperienceManagers) {
      return customerExperienceManagers.map(c => ({ label: c.firstLastName, value: c.employeeId}))
      .sort((a,b) => a.label.localeCompare(b.label));
    }
  }
);

const getAllCustomerExperienceDirectors = state => state.employee.customerExperienceDirectors;

export const allCustomerExperienceDirectorsSelector = createSelector(
  [getAllCustomerExperienceDirectors],
  customerExperienceDirectors => {
    if (customerExperienceDirectors) {
      return customerExperienceDirectors.map(c => ({ label: c.firstLastName, value: c.employeeId}))
      .sort((a,b) => a.label.localeCompare(b.label));
    }
  }
);

const getCountyBoundaries = state => state.region.countyBoundaries;

export const allCountiesFromBoundaries = createSelector(
  [getCountyBoundaries],
  actions => {
    // Need to check for keys because initial state sets this as an empty object...
    if (actions && Object.keys(actions).length) {
      return Object.values(actions)?.map(x => ({
        label: formatCounty(x.properties.name, x.properties.state),
        value: `${x.properties.id}`,
        state: x.properties.state,
        name: x.properties.name,
      }));
    }
  }
);

const getServiceRegions = state => state.region.serviceRegions;

export const serviceRegionSelector = createSelector(
  [getServiceRegions],
  regions => {
    if (regions) {
      return regions.map(r => ({ label: r.name, value: r.id}));
    }
  }
);

const getFieldOperationsManagers = state => state.employee.fieldOperationsManagers;

export const fieldOperationsManagersSelector = createSelector(
  [getFieldOperationsManagers],
  fieldOperationsManagers => {
    if (fieldOperationsManagers) {
      return fieldOperationsManagers.map(x => ({ label: x.firstLastName, value: x.internetUserId }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }
);
